import type { FC, MouseEvent } from 'react';
import React, { memo, useMemo } from 'react';
import { useNamedContainersWithChildren } from '@alfa-bank/site-named-container/utils';
import cn from 'classnames';

import type { TTag } from './models';

const noop = () => {};

const textSizeMapped = {
    xxs: 'primary-small',
    xs: 'primary-small',
    s: 'primary-medium',
    m: 'primary-medium',
    l: 'primary-large',
} as const;

export const Tag: FC<TTag> = memo(
    ({
        CoreTag,
        Text,
        className,
        classNames,
        children: childrenWithNamedContainers,
        dataTestId,
        disabled,
        forwardedRef,
        label,
        onClick = noop,
        selected,
        size = 'm',
        styles,
        tabIndex,
        role,
        returnEvent,
        allOnclickArguments = false,
        value,
        inline,
        theme = 'white',
        rightAddons: leftAddonsProp,
        leftAddons: rightAddonsProp,
    }) => {
        const isSelected = value === selected;

        const handleClick = (event: MouseEvent<HTMLButtonElement>) => {
            if (allOnclickArguments) {
                onClick(event, value);

                return;
            }
            onClick(returnEvent ? event : value);
        };

        const {
            children,
            leftAddons = leftAddonsProp,
            rightAddons = rightAddonsProp,
        } = useNamedContainersWithChildren(childrenWithNamedContainers);

        const tagChildren = useMemo(() => {
            if (!children || (Array.isArray(children) && children.length === 0)) return label;

            return children;
        }, [children, label]);

        return (
            <CoreTag
                ref={forwardedRef}
                checked={isSelected}
                className={cn(styles.external, styles.tag, classNames, className)}
                colors={theme === 'white' ? 'default' : 'inverted'}
                data-test-selected={isSelected || undefined}
                dataTestId={dataTestId}
                disabled={disabled}
                leftAddons={leftAddons}
                onClick={handleClick}
                rightAddons={rightAddons}
                role={role}
                size={size}
                tabIndex={tabIndex}
                view="filled"
            >
                <Text
                    className={cn(styles.external, styles.text, {
                        [styles.block]: !inline,
                    })}
                    styler={{ margin: 0 }}
                    tag="span"
                    view={textSizeMapped[size]}
                >
                    {tagChildren}
                </Text>
            </CoreTag>
        );
    }
);
